import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Plus, RefreshCw, Trash2, Edit, Download, Upload, Users } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

// 顧客表示ラベルの既定候補（マスタ未取得時のフォールバック）
const DEFAULT_DISPLAY_LABELS = [
  "新入荷",
  "旬",
  "おすすめ",
  "食育",
  "期間限定",
  "人気",
];

export default function ProductsManagement() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [displayLabels, setDisplayLabels] = useState(DEFAULT_DISPLAY_LABELS);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncingProductId, setSyncingProductId] = useState(null);
  const [storesDialogOpen, setStoresDialogOpen] = useState(false);
  const [storesProduct, setStoresProduct] = useState(null);
  const [storesData, setStoresData] = useState(null);
  const [storesLoading, setStoresLoading] = useState(false);

  const handleShowStores = async (product) => {
    setStoresProduct(product);
    setStoresData(null);
    setStoresDialogOpen(true);
    setStoresLoading(true);
    try {
      const res = await axios.get(`${API}/products/${product.id}/stores`, getAuthHeaders());
      setStoresData(res.data);
    } catch (e) {
      toast.error("顧客の取得に失敗しました");
      setStoresDialogOpen(false);
    } finally {
      setStoresLoading(false);
    }
  };

  // 商品差し替え / 発注リストから削除
  const [swapStoreId, setSwapStoreId] = useState(null);
  const [swapQuery, setSwapQuery] = useState("");
  const [swapResults, setSwapResults] = useState([]);
  const [swapSelected, setSwapSelected] = useState(null);
  const [swapQty, setSwapQty] = useState("");
  const [swapUnit, setSwapUnit] = useState("");
  const [swapBusy, setSwapBusy] = useState(false);

  useEffect(() => {
    const q = swapQuery.trim();
    if (!q) { setSwapResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/products/paginated?search=${encodeURIComponent(q)}&page_size=15`, getAuthHeaders());
        setSwapResults(res.data?.items || []);
      } catch (e) { setSwapResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [swapQuery]);

  const openSwap = (storeId) => {
    setSwapStoreId(storeId);
    setSwapQuery(""); setSwapResults([]); setSwapSelected(null); setSwapQty(""); setSwapUnit("");
  };

  const doSwap = async (store) => {
    if (!swapSelected) return toast.error("差し替え先の商品を選んでください");
    setSwapBusy(true);
    try {
      const body = { store_id: store.id, from_product_id: storesProduct.id, to_product_id: swapSelected.id };
      if (String(swapQty).trim() !== "") body.quantity = parseFloat(swapQty);
      if (String(swapUnit).trim() !== "") body.unit = swapUnit.trim();
      const res = await axios.post(`${API}/admin/orders/swap-product`, body, getAuthHeaders());
      toast.success(`${store.name}: ${res.data.swapped}件の発注を「${swapSelected.name}」へ差し替えました`);
      setSwapStoreId(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "差し替えに失敗しました");
    } finally { setSwapBusy(false); }
  };

  const doRemoveFromStore = async (store) => {
    if (!window.confirm(`「${storesProduct.name}」を ${store.name} の発注リストから削除しますか？`)) return;
    try {
      await axios.post(`${API}/admin/stores/remove-product`, { product_id: storesProduct.id, store_ids: [store.id] }, getAuthHeaders());
      toast.success("発注リストから削除しました");
      handleShowStores(storesProduct);
    } catch (e) { toast.error("削除に失敗しました"); }
  };

  const doRemoveFromAll = async () => {
    if (!window.confirm(`「${storesProduct.name}」を、この商品を持つ全店舗の発注リストから一括削除しますか？`)) return;
    try {
      const res = await axios.post(`${API}/admin/stores/remove-product`, { product_id: storesProduct.id }, getAuthHeaders());
      toast.success(`${res.data.updated_stores}店舗の発注リストから削除しました`);
      handleShowStores(storesProduct);
    } catch (e) { toast.error("一括削除に失敗しました"); }
  };

  const [formData, setFormData] = useState({
    code: "",
    name: "",
    unit: "",
    category_id: "",
    estimated_weight: "",
    group_name: "",
    search_keywords: "",
    has_special_lead_time: false,
    special_lead_days: "",
    change_not_allowed: false,
    early_pull: false,
    tax_rate: "",
    display_labels: [],
    prices: {
      a1: "",
      b2: "",
      c3: "",
      d4: "",
      e5: "",
    },
  });

  // フィルタ・ページネーション
  const [categoryFilter, setCategoryFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [reloadFlag, setReloadFlag] = useState(0);
  const PAGE_SIZE = 50;

  // カテゴリは一度だけ取得
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/categories`, getAuthHeaders());
        setCategories(res.data);
      } catch (error) {
        toast.error("カテゴリの取得に失敗しました");
      }
      try {
        const labRes = await axios.get(`${API}/product-labels`, getAuthHeaders());
        const names = (labRes.data || []).map((l) => l.name);
        if (names.length) setDisplayLabels(names);
      } catch (e) {
        // フォールバック（既定ラベル）を使用
      }
    })();
  }, []);

  // 検索語のデバウンス（入力から300ms）
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // 商品をページ単位で取得（ページ・検索・カテゴリ・再読込フラグ変化時）
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
          page_size: String(PAGE_SIZE),
        });
        if (debouncedSearch.trim()) params.append("search", debouncedSearch.trim());
        if (categoryFilter) params.append("category_id", categoryFilter);
        const res = await axios.get(
          `${API}/products/paginated?${params.toString()}`,
          getAuthHeaders()
        );
        setProducts(res.data.items || []);
        setTotal(res.data.total || 0);
      } catch (error) {
        toast.error("商品の取得に失敗しました");
      } finally {
        setLoading(false);
      }
    })();
  }, [page, debouncedSearch, categoryFilter, reloadFlag]);

  const reloadProducts = () => setReloadFlag((f) => f + 1);

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        code: product.code || "",
        name: product.name || "",
        unit: product.unit || "",
        category_id: product.category_id || "",
        estimated_weight: product.estimated_weight || "",
        group_name: product.group_name || "",
        search_keywords: (product.search_keywords || []).join(" "),
        has_special_lead_time: product.has_special_lead_time || false,
        special_lead_days: product.special_lead_days || "",
        change_not_allowed: product.change_not_allowed || false,
        early_pull: product.early_pull || false,
        tax_rate: product.tax_rate === null || product.tax_rate === undefined ? "" : String(product.tax_rate),
        display_labels: product.display_labels || [],
        prices: {
          a1: product.prices?.a1 || "",
          b2: product.prices?.b2 || "",
          c3: product.prices?.c3 || "",
          d4: product.prices?.d4 || "",
          e5: product.prices?.e5 || "",
        },
      });
    } else {
      setEditingProduct(null);
      setFormData({
        code: "",
        name: "",
        unit: "",
        category_id: "",
        estimated_weight: "",
        group_name: "",
        search_keywords: "",
        has_special_lead_time: false,
        special_lead_days: "",
        change_not_allowed: false,
        display_labels: [],
        prices: {
          a1: "",
          b2: "",
          c3: "",
          d4: "",
          e5: "",
        },
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // バリデーション
    if (!formData.code.trim() || !formData.name.trim() || !formData.unit.trim() || !formData.category_id) {
      toast.error("商品コード、商品名、単位、カテゴリは必須です");
      return;
    }

    try {
      // 価格データの整形
      const prices = {};
      Object.keys(formData.prices).forEach((rank) => {
        const value = formData.prices[rank];
        if (value !== "" && value !== null && value !== undefined) {
          prices[rank] = parseFloat(value) || 0;
        }
      });

      const payload = {
        code: formData.code.trim(),
        name: formData.name.trim(),
        unit: formData.unit.trim(),
        category_id: formData.category_id,
        estimated_weight: formData.estimated_weight?.trim() || null,
        group_name: formData.group_name?.trim() || "",
        search_keywords: (formData.search_keywords || "")
          .split(/[\s,、，・]+/)
          .map((s) => s.trim())
          .filter(Boolean),
        has_special_lead_time: formData.has_special_lead_time,
        special_lead_days: formData.has_special_lead_time && formData.special_lead_days
          ? parseInt(formData.special_lead_days)
          : null,
        change_not_allowed: formData.change_not_allowed,
        early_pull: formData.early_pull,
        tax_rate: formData.tax_rate === "" ? null : parseFloat(formData.tax_rate),
        display_labels: formData.display_labels,
        prices: prices,
      };

      if (editingProduct) {
        await axios.put(
          `${API}/products/${editingProduct.id}`,
          payload,
          getAuthHeaders()
        );
        toast.success("商品を更新しました");
      } else {
        await axios.post(`${API}/products`, payload, getAuthHeaders());
        toast.success("商品を作成しました（価格はやさまるデスクから自動取得されます）");
      }

      setDialogOpen(false);
      reloadProducts();
    } catch (error) {
      const message = error.response?.data?.detail || "操作に失敗しました";
      toast.error(message);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm("この商品を削除してもよろしいですか？")) {
      return;
    }

    try {
      await axios.delete(`${API}/products/${productId}`, getAuthHeaders());
      toast.success("商品を削除しました");
      reloadProducts();
    } catch (error) {
      toast.error("削除に失敗しました");
    }
  };

  const handleSyncAllPrices = async () => {
    if (!window.confirm("全商品の価格をやさまるデスクから同期しますか？\n（デスクで新しく作成された商品も自動で追加されます）")) {
      return;
    }

    setSyncing(true);
    try {
      const res = await axios.post(
        `${API}/admin/sync-prices`,
        {},
        getAuthHeaders()
      );
      toast.success(res.data.message || "価格を同期しました");
      reloadProducts();
    } catch (error) {
      const message = error.response?.data?.detail || "同期に失敗しました";
      toast.error(message);
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncSinglePrice = async (productId) => {
    setSyncingProductId(productId);
    try {
      const res = await axios.post(
        `${API}/admin/sync-prices/${productId}`,
        {},
        getAuthHeaders()
      );
      toast.success(res.data.message || "価格を同期しました");
      reloadProducts();
    } catch (error) {
      const message = error.response?.data?.detail || "同期に失敗しました";
      toast.error(message);
    } finally {
      setSyncingProductId(null);
    }
  };

  const handleLabelToggle = (label) => {
    setFormData((prev) => {
      const labels = prev.display_labels || [];
      if (labels.includes(label)) {
        return { ...prev, display_labels: labels.filter((l) => l !== label) };
      } else {
        return { ...prev, display_labels: [...labels, label] };
      }
    });
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find((c) => c.id === categoryId);
    return cat ? cat.name : "未分類";
  };

  const [importingCsv, setImportingCsv] = useState(false);

  const handleExportCsv = async () => {
    try {
      const res = await axios.get(`${API}/products/export-csv`, {
        ...getAuthHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "products.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSVをダウンロードしました");
    } catch (error) {
      toast.error("CSVのダウンロードに失敗しました");
    }
  };

  const handleImportCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImportingCsv(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/products/import-csv`, fd, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data",
        },
      });
      const d = res.data || {};
      toast.success(
        `${d.updated}件を更新しました（コード不一致 ${d.unmatched}件 / 未知カテゴリ ${d.unknown_category}件${d.invalid_lead ? ` / リードタイムが数字でない ${d.invalid_lead}件` : ""}はそのまま）`
      );
      reloadProducts();
    } catch (error) {
      toast.error("CSV取込に失敗しました");
    } finally {
      setImportingCsv(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">商品管理</h1>
        <div className="flex gap-2">
          <Button onClick={handleExportCsv} variant="outline" data-testid="products-export-csv">
            <Download className="h-4 w-4 mr-2" />
            商品CSVダウンロード
          </Button>
          <label>
            <input
              type="file"
              accept=".csv"
              onChange={handleImportCsv}
              className="hidden"
              id="products-csv-import"
            />
            <Button
              variant="outline"
              disabled={importingCsv}
              data-testid="products-import-csv"
              onClick={() => document.getElementById("products-csv-import")?.click()}
            >
              <Upload className="h-4 w-4 mr-2" />
              {importingCsv ? "取込中..." : "CSV取込（一括更新）"}
            </Button>
          </label>
          <Button
            onClick={handleSyncAllPrices}
            disabled={syncing}
            variant="outline"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
            全商品の価格を同期
          </Button>
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="h-4 w-4 mr-2" />
            新規商品を追加
          </Button>
        </div>
      </div>

      {/* フィルタ・検索 */}
      <div className="mb-4 flex flex-wrap gap-4 items-end">
        <div className="w-72">
          <Label>商品名・コードで検索</Label>
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="商品名またはコードを入力"
            data-testid="product-search-input"
          />
        </div>
        <div className="w-64">
          <Label>カテゴリでフィルタ</Label>
          <Select
            value={categoryFilter || "all"}
            onValueChange={(val) => {
              setCategoryFilter(val === "all" ? "" : val);
              setPage(1);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="すべて" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">すべて</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="text-sm text-gray-600 pb-2" data-testid="product-total-count">
          全 <span className="font-bold">{total.toLocaleString()}</span> 件
          {loading && <span className="ml-2 text-emerald-600">読み込み中...</span>}
        </div>
      </div>

      {/* 商品一覧テーブル */}
      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium">商品コード</th>
                <th className="px-4 py-3 text-left text-sm font-medium">商品名</th>
                <th className="px-4 py-3 text-left text-sm font-medium">単位</th>
                <th className="px-4 py-3 text-left text-sm font-medium">目安重量</th>
                <th className="px-4 py-3 text-left text-sm font-medium">カテゴリ</th>
                <th className="px-4 py-3 text-left text-sm font-medium">A1</th>
                <th className="px-4 py-3 text-left text-sm font-medium">B2</th>
                <th className="px-4 py-3 text-left text-sm font-medium">C3</th>
                <th className="px-4 py-3 text-left text-sm font-medium">D4</th>
                <th className="px-4 py-3 text-left text-sm font-medium">E5</th>
                <th className="px-4 py-3 text-left text-sm font-medium">ラベル</th>
                <th className="px-4 py-3 text-left text-sm font-medium">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan="12" className="px-4 py-8 text-center text-gray-500">
                    読み込み中...
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan="12" className="px-4 py-8 text-center text-gray-500">
                    商品がありません
                  </td>
                </tr>
              ) : (
                products.map((product) => (
                  <tr key={product.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">{product.code}</td>
                    <td className="px-4 py-3 text-sm font-medium">{product.name}</td>
                    <td className="px-4 py-3 text-sm">{product.unit}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{product.estimated_weight || "-"}</td>
                    <td className="px-4 py-3 text-sm">{getCategoryName(product.category_id)}</td>
                    <td className="px-4 py-3 text-sm">
                      {product.prices?.a1 ? `¥${product.prices.a1}` : "-"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {product.prices?.b2 ? `¥${product.prices.b2}` : "-"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {product.prices?.c3 ? `¥${product.prices.c3}` : "-"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {product.prices?.d4 ? `¥${product.prices.d4}` : "-"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {product.prices?.e5 ? `¥${product.prices.e5}` : "-"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex flex-wrap gap-1">
                        {product.early_pull && (
                          <span
                            data-testid={`product-early-pull-badge-${product.id}`}
                            className="px-2 py-0.5 bg-sky-100 text-sky-800 text-xs rounded border border-sky-300 font-medium"
                          >
                            🐟 事前取込
                          </span>
                        )}
                        {product.display_labels?.map((label) => (
                          <span
                            key={label}
                            className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded"
                          >
                            {label}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleShowStores(product)}
                          title="紐づく顧客を見る"
                          data-testid={`product-stores-btn-${product.id}`}
                        >
                          <Users className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSyncSinglePrice(product.id)}
                          disabled={syncingProductId === product.id}
                          title="価格を再同期"
                        >
                          <RefreshCw
                            className={`h-3 w-3 ${
                              syncingProductId === product.id ? "animate-spin" : ""
                            }`}
                          />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleOpenDialog(product)}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(product.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ページネーション */}
      <div className="mt-4 flex items-center justify-between" data-testid="products-pagination">
        <div className="text-sm text-gray-600">
          {total > 0
            ? `${(page - 1) * PAGE_SIZE + 1} - ${Math.min(page * PAGE_SIZE, total)} / ${total.toLocaleString()} 件`
            : "0 件"}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
            data-testid="products-prev-page"
          >
            前へ
          </Button>
          <span className="text-sm text-gray-700">
            {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading}
            data-testid="products-next-page"
          >
            次へ
          </Button>
        </div>
      </div>

      {/* 紐づく顧客ダイアログ */}
      <Dialog open={storesDialogOpen} onOpenChange={setStoresDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="product-stores-dialog">
          <DialogHeader>
            <DialogTitle>
              紐づく顧客
              {storesProduct ? (
                <span className="block text-sm font-normal text-gray-500 mt-1">
                  {storesProduct.name}
                  {storesProduct.code ? `（${storesProduct.code}）` : ""}
                </span>
              ) : null}
            </DialogTitle>
          </DialogHeader>
          {storesLoading ? (
            <div className="py-8 text-center text-sm text-gray-400">読み込み中...</div>
          ) : storesData ? (
            <div className="space-y-4">
              <div>
                <div className="text-sm font-medium text-gray-700 mb-1.5">
                  発注リストに登録済みの顧客（{storesData.assigned_count}件）
                </div>
                {storesData.assigned_stores.length === 0 ? (
                  <div className="text-sm text-gray-400 border border-dashed rounded-lg py-4 text-center">
                    この商品を発注リストに登録している顧客はいません
                  </div>
                ) : (
                  <div className="border rounded-lg divide-y max-h-72 overflow-y-auto" data-testid="product-assigned-stores">
                    {storesData.assigned_stores.map((s) => (
                      <div key={s.id} className="px-3 py-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-gray-400 shrink-0">{s.code}</span>
                          <span className="text-gray-800 flex-1 truncate">{s.name}</span>
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openSwap(s.id)} data-testid={`swap-btn-${s.id}`}>商品差し替え</Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-500 hover:text-rose-600" onClick={() => doRemoveFromStore(s)} data-testid={`remove-store-btn-${s.id}`}>リストから削除</Button>
                        </div>
                        {swapStoreId === s.id && (
                          <div className="mt-2 p-2 bg-slate-50 border rounded-md space-y-2" data-testid={`swap-panel-${s.id}`}>
                            <div className="text-xs text-slate-500">この店舗の「{storesProduct.name}」の<strong>本日以降の納品分</strong>の発注を、選んだ商品へ差し替えます（締切後も可。過去の納品分は変更しません）。</div>
                            <Input value={swapQuery} onChange={(e) => setSwapQuery(e.target.value)} placeholder="差し替え先の商品を検索（名称・コード・検索ワード）" className="h-8 text-sm" data-testid="swap-search" lang="ja" />
                            {swapResults.length > 0 && !swapSelected && (
                              <div className="border rounded max-h-40 overflow-y-auto bg-white divide-y">
                                {swapResults.map((p) => (
                                  <button key={p.id} type="button" onClick={() => { setSwapSelected(p); setSwapUnit(p.unit || ""); }} className="w-full text-left px-2 py-1.5 text-sm hover:bg-slate-50" data-testid={`swap-option-${p.id}`}>
                                    {p.name} <span className="font-mono text-xs text-gray-400">{p.code}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                            {swapSelected && (
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm text-emerald-700">→ {swapSelected.name}</span>
                                <button type="button" className="text-xs text-slate-500 underline" onClick={() => setSwapSelected(null)}>選び直す</button>
                                <Input value={swapQty} onChange={(e) => setSwapQty(e.target.value)} placeholder="数量(任意)" type="number" step="0.01" className="h-8 w-24 text-sm" data-testid="swap-qty" />
                                <Input value={swapUnit} onChange={(e) => setSwapUnit(e.target.value)} placeholder="単位" className="h-8 w-20 text-sm" data-testid="swap-unit" />
                              </div>
                            )}
                            <div className="flex justify-end gap-2">
                              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setSwapStoreId(null)}>キャンセル</Button>
                              <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" disabled={swapBusy || !swapSelected} onClick={() => doSwap(s)} data-testid={`swap-confirm-${s.id}`}>差し替える</Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {storesData.assigned_count > 0 && (
                  <div className="mt-2 text-right">
                    <Button size="sm" variant="outline" className="h-8 text-xs text-rose-600 border-rose-300 hover:bg-rose-50" onClick={doRemoveFromAll} data-testid="remove-all-btn">
                      この商品を全店舗の発注リストから一括削除
                    </Button>
                  </div>
                )}
              </div>
              {storesData.all_products_count > 0 && (
                <div className="text-xs text-gray-500 bg-gray-50 border rounded-lg px-3 py-2">
                  ※ このほかに、発注リスト未設定（＝全商品を表示）の顧客が <strong>{storesData.all_products_count}件</strong> あります。これらの顧客にもこの商品は表示されます。
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* 商品作成・編集ダイアログ */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProduct ? "商品を編集" : "新規商品を追加"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* 基本情報 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="code">商品コード *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) =>
                    setFormData({ ...formData, code: e.target.value })
                  }
                  required
                  disabled={!!editingProduct}
                />
              </div>
              <div>
                <Label htmlFor="name">商品名 *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="unit">単位 *</Label>
                <Input
                  id="unit"
                  value={formData.unit}
                  onChange={(e) =>
                    setFormData({ ...formData, unit: e.target.value })
                  }
                  placeholder="例: kg, 個, 束"
                  required
                />
              </div>
              <div>
                <Label htmlFor="category">カテゴリ *</Label>
                <Select
                  value={formData.category_id}
                  onValueChange={(value) =>
                    setFormData({ ...formData, category_id: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="カテゴリを選択" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* 目安重量 */}
            <div>
              <Label htmlFor="estimated_weight">目安重量</Label>
              <Input
                id="estimated_weight"
                value={formData.estimated_weight}
                onChange={(e) =>
                  setFormData({ ...formData, estimated_weight: e.target.value })
                }
                placeholder="例: 約1kg, 200g前後"
              />
              <p className="text-xs text-slate-500 mt-1">
                発注画面で顧客に表示されます
              </p>
            </div>

            {/* グループ名（手動指定） */}
            <div>
              <Label htmlFor="group_name">グループ名（任意）</Label>
              <Input
                id="group_name"
                data-testid="product-group-name-input"
                value={formData.group_name}
                onChange={(e) =>
                  setFormData({ ...formData, group_name: e.target.value })
                }
                placeholder="例: バナナ（空欄なら商品名から自動判定）"
              />
              <p className="text-xs text-slate-500 mt-1">
                同名・単位違いの商品をまとめる名前。自動判定が合わない場合に同じ名前を入力するとグループ化されます。空欄なら商品名から自動判定します。
              </p>
            </div>

            {/* 検索キーワード（別名・よみがな） */}
            <div>
              <Label htmlFor="search_keywords">検索キーワード（別名・よみがな）</Label>
              <Input
                id="search_keywords"
                data-testid="product-search-keywords-input"
                value={formData.search_keywords}
                onChange={(e) =>
                  setFormData({ ...formData, search_keywords: e.target.value })
                }
                placeholder="例: ねぎ 青ねぎ 葱（スペース区切りで複数登録）"
              />
              <p className="text-xs text-slate-500 mt-1">
                漢字の読みや略称・俗称で検索ヒットさせます。顧客には表示されず、検索のみに使われます。スペースや「、」で区切って複数登録できます。
              </p>
            </div>

            {/* 特殊リードタイム */}
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <Checkbox
                  id="has_special_lead_time"
                  checked={formData.has_special_lead_time}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, has_special_lead_time: checked })
                  }
                />
                <Label htmlFor="has_special_lead_time">特殊リードタイムあり</Label>
              </div>
              {formData.has_special_lead_time && (
                <div className="ml-6">
                  <Label htmlFor="special_lead_days">リードタイム（日数）</Label>
                  <Input
                    id="special_lead_days"
                    type="number"
                    value={formData.special_lead_days}
                    onChange={(e) =>
                      setFormData({ ...formData, special_lead_days: e.target.value })
                    }
                    placeholder="例: 3"
                  />
                </div>
              )}
            </div>

            {/* 変更禁止フラグ */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="change_not_allowed"
                checked={formData.change_not_allowed}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, change_not_allowed: checked })
                }
              />
              <Label htmlFor="change_not_allowed">注文後の変更を禁止</Label>
            </div>

            {/* 事前取込対象（魚類など） */}
            <div className="rounded-lg border border-sky-200 bg-sky-50/60 p-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="early_pull"
                  data-testid="product-early-pull-checkbox"
                  checked={formData.early_pull}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, early_pull: checked })
                  }
                />
                <Label htmlFor="early_pull" className="font-medium text-sky-800">
                  🐟 事前取込対象（魚類など）
                </Label>
              </div>
              <p className="text-xs text-sky-700 mt-1 ml-6">
                この商品を含む発注は、要対応ボックス・発注確認画面で目立つように表示され、締切前でも管理者が手動で「事前確定」してやさまるデスクへ先に取り込めます。
              </p>
            </div>

            {/* 消費税率 */}
            <div>
              <Label htmlFor="tax_rate">消費税率（売上集計用）</Label>
              <select
                id="tax_rate"
                data-testid="product-tax-rate-select"
                value={formData.tax_rate}
                onChange={(e) => setFormData({ ...formData, tax_rate: e.target.value })}
                className="mt-1 w-full h-10 px-3 border border-slate-300 rounded-md text-sm bg-white"
              >
                <option value="">未設定（8%として集計）</option>
                <option value="8">8%（軽減税率・食品）</option>
                <option value="10">10%（標準）</option>
              </select>
              <p className="text-xs text-slate-500 mt-1">
                売上集計ページの税額計算に使用します。未設定の商品は 8% として集計されます。
              </p>
            </div>

            {/* 顧客表示ラベル */}
            <div>
              <Label>顧客表示ラベル（複数選択可）</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {displayLabels.map((label) => (
                  <div key={label} className="flex items-center space-x-2">
                    <Checkbox
                      id={`label-${label}`}
                      checked={formData.display_labels.includes(label)}
                      onCheckedChange={() => handleLabelToggle(label)}
                    />
                    <Label htmlFor={`label-${label}`} className="font-normal">
                      {label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* 価格（5段階） */}
            <div>
              <Label>価格（ランク別）</Label>
              <p className="text-sm text-gray-500 mb-2">
                {editingProduct
                  ? "価格は手動で編集可能です"
                  : "新規作成時は、やさまるデスクから自動取得されます（手動入力も可）"}
              </p>
              <div className="grid grid-cols-5 gap-2">
                {["a1", "b2", "c3", "d4", "e5"].map((rank) => (
                  <div key={rank}>
                    <Label htmlFor={`price-${rank}`} className="text-xs uppercase">
                      {rank}
                    </Label>
                    <Input
                      id={`price-${rank}`}
                      type="number"
                      step="0.01"
                      value={formData.prices[rank]}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          prices: { ...formData.prices, [rank]: e.target.value },
                        })
                      }
                      placeholder="0"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                キャンセル
              </Button>
              <Button type="submit">
                {editingProduct ? "更新" : "作成"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
